<!--
/*
 * Компонент BottomNav — фиксированная нижняя навигация + футер
 */
-->
<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ERouteNames } from '@/router/ERouteNames'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
import AppFooter from '@/components/AppFooter.vue'
import type { INavTab } from '@/components/types/INavTab'
import { isTelegram } from '@/utils/isTelegram'
import { useSupportContact } from '@/components/support-button/useSupportContact'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const authStore = useAuthStore()
const settingsStore = useSettingsStore()
const { t } = useI18n()
const { contactViaTelegram, contactViaEmail } = useSupportContact()

// ── Конфигурация позиций навбара ─────────────────────
const allTabs = computed<INavTab[]>(() => [
  { route: ERouteNames.HOME, icon: 'ri-home-line', activeIcon: 'ri-home-fill', label: t('nav.home'), type: 'route' },
  { route: null, icon: 'ri-chat-3-line', activeIcon: 'ri-chat-3-fill', label: t('nav.support'), type: 'action', id: 'support' },
  { route: null, icon: 'ri-rocket-line', activeIcon: 'ri-rocket-fill', label: t('nav.boost'), type: 'action', id: 'boost', paidOnly: true, telegramOnly: true },
  { route: ERouteNames.PROFILE, icon: 'ri-user-line', activeIcon: 'ri-user-fill', label: t('nav.profile'), type: 'route' },
  { route: ERouteNames.ADMIN_NAV, icon: 'ri-settings-3-line', activeIcon: 'ri-settings-3-fill', label: t('nav.admin'), type: 'route', adminOnly: true },
])

const hiddenRoutes: string[] = [
  ERouteNames.AUTH,
  ERouteNames.PAYMENT,
  ERouteNames.MAGIC_LINK,
  ERouteNames.NOT_FOUND,
]

const isVisible = computed(() => {
  if (!authStore.isAuth) return false
  const currentName = route.name as string
  return !hiddenRoutes.includes(currentName)
})

const visibleTabs = computed(() => {
  return allTabs.value.filter((tab) => {
    if (tab.adminOnly && !userStore.user?.isAdmin) return false
    if (tab.paidOnly && !userStore.user?.isTariffActive) return false
    if (tab.telegramOnly && !isTelegram()) return false
    return true
  })
})

const activeTab = computed(() => {
  return route.name as string
})

const handleTabClick = (tab: INavTab) => {
  if (tab.type === 'route' && tab.route && route.name !== tab.route) {
    router.push({ name: tab.route })
  }
}

const handleActionClick = (tab: INavTab) => {
  if (tab.id === 'boost') {
    boostTelegram()
  }
}

const boostTelegram = () => {
  let url = settingsStore.proxyURLs[0]
  if (!url) return
  if (url.startsWith('tg://')) {
    url = url.replace('tg://', 'https://t.me/')
  }
  if (window.Telegram?.WebApp?.openTelegramLink) {
    window.Telegram.WebApp.openTelegramLink(url)
  }
}
</script>

<template>
  <div v-if="isVisible" class="bottom-bar">
    <!-- Позиции навигации -->
    <div class="bottom-bar__tabs">
      <template v-for="tab in visibleTabs" :key="tab.label">
        <!-- Поддержка — v-menu с выбором TG/Email -->
        <v-menu v-if="tab.id === 'support'" location="top">
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="bottom-bar__tab"
              data-onboarding="support"
            >
              <v-icon size="24">{{ tab.icon }}</v-icon>
              <span class="bottom-bar__label">{{ tab.label }}</span>
            </button>
          </template>
          <v-list density="compact">
            <v-list-item
              prepend-icon="ri-telegram-line"
              title="Telegram"
              @click="contactViaTelegram"
            />
            <v-list-item
              prepend-icon="ri-mail-line"
              title="Email"
              @click="contactViaEmail"
            />
          </v-list>
        </v-menu>

        <!-- Action-позиции -->
        <button
          v-else-if="tab.type === 'action'"
          class="bottom-bar__tab"
          @click="handleActionClick(tab)"
        >
          <v-icon size="24">{{ tab.icon }}</v-icon>
          <span class="bottom-bar__label">{{ tab.label }}</span>
        </button>

        <!-- Route-позиции -->
        <button
          v-else
          class="bottom-bar__tab"
          :class="{ 'bottom-bar__tab--active': activeTab === tab.route }"
          :data-onboarding="tab.route === ERouteNames.PROFILE ? 'profile' : undefined"
          @click="handleTabClick(tab)"
        >
          <v-icon size="24">{{ activeTab === tab.route ? tab.activeIcon : tab.icon }}</v-icon>
          <span class="bottom-bar__label">{{ tab.label }}</span>
        </button>
      </template>
    </div>

    <!-- Футер: разработка + версия -->
    <app-footer />
  </div>
</template>

<style scoped>
.bottom-bar {
  position: fixed;
  bottom: 0;
  width: 100%;
  max-width: var(--app-max-width);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1004;
  background: rgb(var(--v-theme-surface));
}

.bottom-bar__tabs {
  display: flex;
  height: 56px;
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bottom-bar__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: color 0.2s;
}

.bottom-bar__tab--active {
  color: rgb(var(--v-theme-primary));
}

.bottom-bar__label {
  font-size: 11px;
  line-height: 1;
}
</style>
